/*
 * One coordinator for both cart views. Only the two offer attributes are patched.
 * Dawn mutations and attribute reconciliation share a queue; window.fetch is never replaced.
 */
(() => {
  const config = document.getElementById('FreebieOfferConfig');
  if (!config || window.freebieOffer) return;

  class FreebieOfferController {
    constructor(data) {
      this.enabled = data.enabled === 'true';
      this.minimum = Number(data.minimum);
      this.successMessage = data.successMessage;
      this.cartUrl = `${data.cartUrl}.js`;
      this.updateUrl = `${data.updateUrl.replace(/\.js$/, '')}.js`;
      this.queue = Promise.resolve();
      this.cart = {
        item_count: Number(data.itemCount),
        items_subtotal_price: Number(data.subtotal),
        currency: data.currency,
        attributes: JSON.parse(data.attributes || '{}'),
      };
      this.lastEdit = 0;
      this.failed = false;

      // Opt-in wrapper used only at Dawn's existing mutation call sites.
      // Return the original Response so Dawn's rendering, errors and analytics stay intact.
      this.cartFetch = (url, options) => this.enqueue(async () => {
        const response = await fetch(url, options);
        if (response.ok) {
          try {
            const result = await response.clone().json();
            if (!result.errors && !result.status) {
              // /add returns added lines, not a complete cart.
              if (this.isCart(result)) {
                await this.reconcile(result);
              } else if (this.enabled || this.hasSavedAttributes()) {
                await this.reconcile(await this.readCart());
              }
            }
          } catch (error) {
            // An offer failure must not turn a successful item/note change into a cart error.
            this.reportFailure(error);
          }
        }
        return response;
      });
    }

    enqueue(task) {
      const result = this.queue.then(task);
      this.queue = result.catch(() => {});
      return result;
    }

    isCart(cart) {
      return cart && Number.isFinite(cart.items_subtotal_price) && Number.isFinite(cart.item_count)
        && typeof cart.currency === 'string' && cart.attributes && typeof cart.attributes === 'object';
    }

    active(cart) {
      // A rupee threshold must never be compared to a different presentment currency.
      return this.enabled && Number.isSafeInteger(this.minimum) && this.minimum > 0 && cart.currency === 'INR';
    }

    eligible(cart) {
      return this.active(cart) && cart.item_count > 0 && cart.items_subtotal_price >= this.minimum;
    }

    hasSavedAttributes() {
      return Boolean(this.cart.attributes.Freebie || this.cart.attributes['Freebie Promotion']);
    }

    attributePatch(cart) {
      const eligible = this.eligible(cart);
      const desired = {
        Freebie: eligible ? '1 Freebie Included' : '',
        'Freebie Promotion': eligible ? `Spend ₹${this.minimum / 100}` : '',
      };
      return Object.fromEntries(Object.entries(desired).filter(([key, value]) =>
        String(cart.attributes[key] || '') !== value
      ));
    }

    async requestJSON(url, options = {}) {
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), 8000);
      try {
        const response = await fetch(url, {
          ...options,
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json', ...options.headers },
          signal: abort.signal,
        });
        if (!response.ok) throw new Error(`Freebie cart request failed (${response.status})`);
        const cart = await response.json();
        if (!this.isCart(cart) || cart.errors) throw new Error('Invalid freebie cart response');
        return cart;
      } finally {
        clearTimeout(timeout);
      }
    }

    readCart() {
      return this.requestJSON(this.cartUrl);
    }

    async reconcile(cart) {
      // Re-evaluate the response, since another tab/integration can change eligibility during a save.
      // Bound reconciliation: never retry forever if a server/integration refuses the attributes.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        this.cart = cart;
        const attributes = this.attributePatch(cart);
        if (!Object.keys(attributes).length) {
          this.failed = false;
          this.render();
          return cart;
        }
        cart = await this.requestJSON(this.updateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
        });
      }
      this.cart = cart;
      if (Object.keys(this.attributePatch(cart)).length) throw new Error('Freebie attributes did not settle');
      this.failed = false;
      this.render();
      return cart;
    }

    refresh() {
      // Drawer opens, pubsub and checkout can request the same read; share a single pending job.
      if (!this.refreshPromise) {
        this.refreshPromise = this.enqueue(async () => this.reconcile(await this.readCart()))
          .finally(() => { this.refreshPromise = null; });
      }
      return this.refreshPromise;
    }

    refreshSafely() {
      return this.refresh().catch((error) => this.reportFailure(error));
    }

    reportFailure(error) {
      this.failed = true;
      this.render();
      console.warn('[Freebie offer]', error);
    }

    formatMoney(minor) {
      return `₹${new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: minor % 100 ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(minor / 100)}`;
    }

    renderElement(element) {
      const cart = this.cart;
      element.hidden = !this.active(cart) || cart.item_count === 0;
      if (element.hidden) return;
      const eligible = this.eligible(cart);
      const amount = Math.max(0, Math.min(this.minimum, cart.items_subtotal_price));
      const message = this.failed ? 'Your freebie details need to be saved.' : eligible
        ? this.successMessage
        : `🎁 Spend ${this.formatMoney(this.minimum - amount)} more to unlock 1 FREEBIE!`;
      const messageElement = element.querySelector('[data-freebie-message]');
      if (messageElement.textContent.trim() !== message) messageElement.textContent = message;
      element.classList.toggle('freebie-offer--eligible', eligible && !this.failed);
      element.querySelector('[data-freebie-reward]').hidden = !eligible || this.failed;
      element.querySelector('[data-freebie-error]').hidden = !this.failed;
      const progress = element.querySelector('[data-freebie-progress]');
      progress.setAttribute('aria-valuemax', this.minimum);
      progress.setAttribute('aria-valuenow', amount);
      progress.setAttribute('aria-valuetext', `${this.formatMoney(amount)} of ${this.formatMoney(this.minimum)}`);
      element.querySelector('[data-freebie-fill]').style.width = `${amount / this.minimum * 100}%`;
    }

    render() {
      document.querySelectorAll('freebie-offer').forEach((element) => this.renderElement(element));
    }

    signature(cart) {
      return JSON.stringify([cart.item_count, cart.items_subtotal_price, cart.currency,
        cart.items?.map((item) => [item.key, item.quantity, item.final_line_price])]);
    }

    async settleCheckout() {
      // Let Dawn's 300ms change debounce and BulkAdd's 250ms queue submit pending edits.
      // Check the queue again after a fresh read: a mutation may have arrived during that read.
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const delay = this.lastEdit + 650 - Date.now();
        if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
        const pending = this.queue;
        await pending;
        if (pending !== this.queue) continue;
        const refresh = this.refresh();
        const queue = this.queue;
        await refresh;
        if (queue === this.queue && Date.now() - this.lastEdit >= 650) {
          if (!this.cart.item_count) throw new Error('Cannot check out an empty cart');
          return;
        }
      }
      throw new Error('The cart is still changing; please try checkout again');
    }

    prepareCheckout() {
      if (!this.enabled && !this.hasSavedAttributes()) return Promise.resolve();
      if (!this.preparingCheckout) {
        this.preparingCheckout = this.settleCheckout().finally(() => { this.preparingCheckout = null; });
      }
      return this.preparingCheckout;
    }

    showCheckoutError() {
      // Generic cart error also works while the promotion is disabled (no promotional UI).
      const message = `${window.cartStrings?.error || 'We could not update your cart.'} Please try checkout again.`;
      document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach((element) => {
        element.setAttribute('role', 'alert');
        element.textContent = message;
      });
    }

    checkout(action) {
      if (this.checkingOut) return this.checkoutPromise;
      this.checkingOut = true;
      this.checkoutPromise = this.prepareCheckout().then(() => {
        this.replayingCheckout = true;
        try { action(); } finally { this.replayingCheckout = false; }
      }).catch((error) => {
        this.reportFailure(error);
        this.showCheckoutError();
      }).finally(() => { this.checkingOut = false; });
      return this.checkoutPromise;
    }

    onGoKwikCheckout(event) {
      // Called by the early layout listener, ahead of GoKwik's capture handler.
      // Replay the existing button after saving; GoKwik still owns its SDK, loader and navigation.
      if (this.replayingCheckout || (!this.enabled && !this.hasSavedAttributes())) return;
      const button = (event.composedPath?.() || []).find((node) =>
        node.classList?.contains('gokwik-marge')
      );
      if (!button || (button.dataset.gokwikFunction || button.dataset.function) !== 'checkout' ||
        button.disabled || button.classList.contains('gokwik-sold-out') || button.classList.contains('processing')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.checkingOut) return;

      const root = button.getRootNode();
      const wasBusy = button.getAttribute('aria-busy');
      button.setAttribute('aria-busy', 'true');
      this.checkout(() => {
        // Dawn can replace the button while a pending quantity edit finishes.
        const currentButton = button.isConnected ? button : button.id && root.getElementById(button.id);
        if (!currentButton || currentButton.disabled || !currentButton.classList.contains('gokwik-marge') ||
          (currentButton.dataset.gokwikFunction || currentButton.dataset.function) !== 'checkout') {
          throw new Error('The cart changed; please try checkout again');
        }
        currentButton.click();
      }).finally(() => {
        if (wasBusy === null) button.removeAttribute('aria-busy');
        else button.setAttribute('aria-busy', wasBusy);
      });
    }

    connect() {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Known Dawn writes already passed through cartFetch. Never use stale event data to save attributes.
        if (event.source === 'product-form' ||
          (this.isCart(event.cartData) && this.signature(event.cartData) === this.signature(this.cart))) {
          this.render();
          return;
        }
        return this.refreshSafely();
      });

      const recordEdit = (event) => {
        if (event.target.matches('quantity-input input, cart-note textarea')) this.lastEdit = Date.now();
      };
      document.addEventListener('input', recordEdit, true);
      document.addEventListener('change', recordEdit, true);

      document.addEventListener('click', (event) => {
        if (event.target.closest('[data-freebie-retry]')) {
          event.preventDefault();
          this.refreshSafely();
          return;
        }
        if ((!this.enabled && !this.hasSavedAttributes()) || this.replayingCheckout || event.button !== 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[href]');
        if (!link) return;
        const url = new URL(link.href, window.location.href);
        if (url.origin !== window.location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.checkout(() => link.click());
      }, true);

      document.addEventListener('submit', (event) => {
        const form = event.target;
        if ((!this.enabled && !this.hasSavedAttributes()) || this.replayingCheckout ||
          !['cart', 'CartDrawer-Form'].includes(form.id)) return;
        if (event.submitter && event.submitter.name !== 'checkout') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const formId = form.id;
        const buttonId = event.submitter?.id;
        this.checkout(() => {
          const currentForm = document.getElementById(formId);
          const button = (buttonId && document.getElementById(buttonId)) ||
            document.querySelector(`[form="${formId}"][name="checkout"]`) || currentForm?.querySelector('[name="checkout"]');
          if (!currentForm || !button || button.disabled) throw new Error('Checkout is unavailable');
          // Preserve Shopify's native POST, note/attribute inputs, validation and checkout submitter.
          currentForm.requestSubmit(button);
        });
      }, true);

      window.addEventListener('pageshow', (event) => { if (event.persisted) this.refreshSafely(); });
      window.addEventListener('focus', () => this.refreshSafely());
      document.addEventListener('shopify:section:load', () => {
        this.render();
        this.refreshSafely();
      });

      this.render();
      if (this.enabled || Object.keys(this.attributePatch(this.cart)).length) this.refreshSafely();
    }
  }

  window.freebieOffer = new FreebieOfferController(config.dataset);
  customElements.define('freebie-offer', class extends HTMLElement {
    connectedCallback() { window.freebieOffer.renderElement(this); }
  });
  window.freebieOffer.connect();
})();
